import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Space, Button, Input, Tag, message, Modal } from 'antd';
import { ReloadOutlined, PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { apiService } from '../../../services/apiService';

type MissingRow = {
  skuId: string
  skuNameZh?: string | null
  skuNameEn?: string | null
  brand?: string | null
  occurrences?: number
  firstSeen?: string
  lastSeen?: string
}

const ShopifyMissingSkus: React.FC = () => {
  const [rows, setRows] = useState<MissingRow[]>([])
  const [selected, setSelected] = useState<React.Key[]>([])
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const url = `/shopify/missing-skus?page=${page}&limit=${pageSize}${query ? `&q=${encodeURIComponent(query)}` : ''}`
      const resp: any = await apiService.get(url)
      if (!resp?.success) throw new Error(resp?.message || 'load failed')
      const list: MissingRow[] = Array.isArray(resp?.data) ? resp.data : (resp?.rows || [])
      setRows(list || [])
      const p = resp?.pagination || {}
      setTotal(Number(p.total || list.length || 0))
    } catch (e) {
      console.error(e)
      message.error('載入缺漏 SKU 失敗')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, query])

  useEffect(() => { fetchData() }, [fetchData])

  const selectedRows = useMemo(() => rows.filter(r => selected.includes(r.skuId)), [rows, selected])

  const handleCreateProducts = async () => {
    if (selected.length === 0) { message.warning('請先選擇要建立的 SKU'); return }
    Modal.confirm({
      title: '確認建立產品',
      icon: <ExclamationCircleOutlined />,
      content: `將自動建立 ${selected.length} 個產品（以 SKU 為 itemCode）。建立後可至產品主檔補充資料。`,
      okText: '建立',
      cancelText: '取消',
      async onOk() {
        try {
          const companyId = localStorage.getItem('current-company-id') || ''
          if (!companyId) { message.error('缺少 companyId，請重新登入'); return }
          const skus = selectedRows.map(r => ({ skuId: r.skuId, itemNameZh: r.skuNameZh || undefined, itemNameEn: r.skuNameEn || undefined, brand: r.brand || undefined }))
          const resp: any = await apiService.createProductsFromMissingSkusShopify({ companyId, skus })
          if (!resp?.success) throw new Error(resp?.message || 'create failed')
          const created = Number(resp?.createdCount || 0)
          message.success(`建立完成（成功 ${created}）`)
          setSelected([])
          fetchData()
        } catch (e) {
          console.error(e)
          message.error('自動建立產品失敗')
        }
      }
    })
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="Shopify 缺漏 SKU">
        <Space style={{ marginBottom: 12 }} wrap>
          <Input.Search placeholder="搜尋 SKU/名稱" allowClear style={{ width: 320 }}
            onSearch={(v)=>{ setPage(1); setQuery(v); }} onChange={(e)=> setQuery(e.target.value)} />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchData}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={selected.length===0} onClick={handleCreateProducts}>
            建立所選 SKU 產品
          </Button>
        </Space>

        <Table
          rowKey={(r)=> r.skuId}
          dataSource={rows}
          loading={loading}
          rowSelection={{ selectedRowKeys: selected, onChange:(keys)=> setSelected(keys) }}
          pagination={{ current: page, pageSize, total, onChange:(p, ps)=>{ setPage(p); setPageSize(ps); } }}
          columns={[
            { title:'SKU', dataIndex:'skuId', width: 220 },
            { title:'名稱(中)', dataIndex:'skuNameZh' },
            { title:'名稱(英)', dataIndex:'skuNameEn' },
            { title:'品牌', dataIndex:'brand', width: 160, render:(v)=> v ? <Tag>{v}</Tag> : '-' },
            { title:'出現次數', dataIndex:'occurrences', width: 120 },
            { title:'最近出現', dataIndex:'lastSeen', width: 200, render:(v)=> v ? new Date(v).toLocaleString() : '-' },
          ]}
        />
      </Card>
    </Space>
  )
}

export default ShopifyMissingSkus


